/* ============================================================================
   Studio-team roles — the matrix below is exactly what the app enforces, and
   it renders as the permission table beside the roster on the Tenders and
   Projects lists. Per-tender / per-project seats keep their own role systems;
   these grades are for teammates who run whole modules with you.
   ========================================================================== */
import type { StudioRole } from "./types";

export const STUDIO_ROLES: StudioRole[] = ["studio-admin", "tender-admin", "tender-coordinator", "site-admin", "comms-manager"];

export const STUDIO_ROLE_LABEL: Record<StudioRole, string> = {
  "studio-admin": "Studio Admin",
  "tender-admin": "Tender Admin",
  "tender-coordinator": "Tender Coordinator",
  "site-admin": "Site Admin",
  "comms-manager": "Communications Manager",
};

export const STUDIO_ROLE_BLURB: Record<StudioRole, string> = {
  "studio-admin": "Runs both modules — every tender and every project, second only to you",
  "tender-admin": "Runs the Bid Manager — opens new tenders, issues ITTs, opens & awards bids",
  "tender-coordinator": "Day-to-day in every tender room — documents, RFIs, alerts; no awards",
  "site-admin": "Runs the Project Manager — creates projects, works in every project room",
  "comms-manager": "Sees every project's RFIs and correspondence, emails and chases the parties, keeps the ball moving — no drawings, money or awards",
};

const G = (sd: boolean, ta: boolean, tc: boolean, sa: boolean, cm: boolean): Record<StudioRole, boolean> =>
  ({ "studio-admin": sd, "tender-admin": ta, "tender-coordinator": tc, "site-admin": sa, "comms-manager": cm });

export const STUDIO_PERMISSIONS: { label: string; grants: Record<StudioRole, boolean> }[] = [
  { label: "See every tender in the Bid Manager",                        grants: G(true, true, true, false, false) },
  { label: "Open (create) new tenders",                                  grants: G(true, true, false, false, false) },
  { label: "Documents, addenda, RFI answers, alerts, quotes, invites",   grants: G(true, true, true, false, false) },
  { label: "Issue the ITT, open sealed bids, share results, award",      grants: G(true, true, false, false, false) },
  { label: "Manage tender client seats & tender teams",                  grants: G(true, true, false, false, false) },
  { label: "See every project in the Project Manager",                   grants: G(true, false, false, true, true) },
  { label: "Create new projects in the Project Manager",                 grants: G(true, false, false, true, false) },
  { label: "Work in every project room (drawings, diary, programme…)",   grants: G(true, false, false, true, false) },
  { label: "Communications hub — every project's RFIs & correspondence in one place", grants: G(true, false, false, true, true) },
  { label: "Answer & coordinate RFIs, run meetings, chase the parties",  grants: G(true, false, false, true, true) },
  { label: "Email correspondence with attachments from the project",     grants: G(true, false, false, true, true) },
  { label: "Invite & manage the studio team itself",                     grants: G(false, false, false, false, false) },   // manager only
];

/** The one grade that opens both modules. Sessions carry it so the landing
    cards, the route guards and the module shells all agree on the answer. */
export function isStudioAdmin(s: { role: string; studioRole?: string } | null): boolean {
  return s?.studioRole === "studio-admin";
}

/** The communications manager: every project, but only its conversations. */
export function isCommsManager(s: { role: string; studioRole?: string } | null): boolean {
  return s?.studioRole === "comms-manager";
}

/** Can this session open brand-new tenders? */
export function canCreateTenders(s: { role: string; tenderId?: string; teamRole?: string } | null): boolean {
  if (!s) return false;
  if (s.role === "manager") return true;
  return s.role === "bidteam" && s.tenderId === "" && s.teamRole === "admin";
}
